export interface Peak {
    peak_index: number;
    peak_label: string;
    peak_height: number;
    peak_area: number;
}

export interface IntensityChartProps {
    peaks: Record<string, Peak>;
    width?: number;
    height?: number;
}

// Fixed display order: TL1, CL1, CL2, CL3, CL4 (anything else appended at the end)
const LABEL_ORDER = ['TL1', 'CL1', 'CL2', 'CL3', 'CL4'];

function orderPeaks(peaks: Peak[]): Peak[] {
    const indexOf = (label: string) => {
        const i = LABEL_ORDER.indexOf(label);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...peaks].sort((left, right) => {
        const li = indexOf(left.peak_label || '');
        const ri = indexOf(right.peak_label || '');
        if (li !== ri) {
            return li - ri;
        }
        return left.peak_index - right.peak_index;
    });
}

function formatPeakValue(value: number): string {
    return Number.isFinite(value) ? value.toFixed(2) : '0.00';
}

export function IntensityChart({ peaks, width = 600, height = 280 }: IntensityChartProps) {
    const peakEntries = orderPeaks(Object.values(peaks));

    if (peakEntries.length === 0) {
        return <p className="text-muted mb-0">No peaks detected.</p>;
    }

    const padding = {
        top: 32,
        right: 16,
        bottom: 56,
        left: 16,
    };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    const maxHeight = Math.max(...peakEntries.map((peak) => peak.peak_height), 1);
    const slotWidth = chartWidth / peakEntries.length;
    const barWidth = Math.min(72, Math.max(28, slotWidth * 0.6));

    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label="Peak intensity bar chart"
            style={{
                width: '100%',
                maxWidth: `${width}px`,
                height: 'auto',
                border: '1px solid #dee2e6',
                borderRadius: '0.375rem',
                backgroundColor: '#fff',
            }}
        >
            {/* Horizontal baseline */}
            <line
                x1={padding.left}
                y1={padding.top + chartHeight}
                x2={width - padding.right}
                y2={padding.top + chartHeight}
                stroke="#ced4da"
                strokeWidth={1}
            />

            {peakEntries.map((peak, index) => {
                const slotCenter = padding.left + slotWidth * (index + 0.5);
                const barX = slotCenter - barWidth / 2;
                const barHeight = (peak.peak_height / maxHeight) * chartHeight;
                const barY = padding.top + chartHeight - barHeight;
                const label = peak.peak_label || `P${index + 1}`;
                const heightText = formatPeakValue(peak.peak_height);
                const areaText = formatPeakValue(peak.peak_area);

                return (
                    <g key={`${label}-${peak.peak_index}-${index}`}>
                        {/* Height label above bar */}
                        <text
                            x={slotCenter}
                            y={barY - 8}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight={600}
                            fill="#212529"
                        >
                            {`h: ${heightText}`}
                        </text>

                        {/* The bar */}
                        <rect
                            x={barX}
                            y={barY}
                            width={barWidth}
                            height={Math.max(barHeight, 1)}
                            fill="#0d6efd"
                            opacity={0.9}
                            rx={4}
                        />

                        {/* Area label inside bar (only if bar is tall enough) */}
                        {barHeight > 24 && (
                            <text
                                x={slotCenter}
                                y={barY + barHeight / 2 + 4}
                                textAnchor="middle"
                                fontSize="10"
                                fill="#fff"
                            >
                                {`A: ${areaText}`}
                            </text>
                        )}

                        {/* Peak label below bar */}
                        <text
                            x={slotCenter}
                            y={padding.top + chartHeight + 22}
                            textAnchor="middle"
                            fontSize="12"
                            fontWeight={600}
                            fill="#495057"
                        >
                            {label}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}
